import { useQuery } from '@tanstack/react-query';
import { Globe, Star, Building2, Calendar, FileText } from 'lucide-react';
import { Drawer } from './Drawer';
import { Badge } from './Badge';
import { leadsApi } from '../lib/api';
import ReactMarkdown from 'react-markdown';

interface LeadProfileDrawerProps {
  leadId: string | null;
  leadName?: string;
  onClose: () => void;
}

export function LeadProfileDrawer({ leadId, leadName, onClose }: LeadProfileDrawerProps) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['lead-profile', leadId],
    queryFn: () => leadsApi.getProfile(leadId!),
    enabled: !!leadId,
  });

  const profile = data?.profile;

  return (
    <Drawer
      isOpen={!!leadId}
      onClose={onClose}
      title={leadName ? `Profile: ${leadName}` : 'Lead Profile'}
      width="xl"
    >
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-2 border-[var(--asi-accent)] border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {error && (
        <div className="text-center py-12">
          <FileText className="w-12 h-12 mx-auto mb-3 text-[var(--asi-text-muted)]" />
          <p className="text-[var(--asi-error)]">Failed to load profile</p>
        </div>
      )}

      {!isLoading && !error && profile && (
        <div className="space-y-6">
          {/* Profile Summary */}
          <div className="flex flex-wrap gap-3">
            {profile.quality_score && (
              <Badge variant="default" size="sm">
                <Star className="w-3 h-3 mr-1 fill-[var(--asi-warning)] text-[var(--asi-warning)]" />
                Quality: {profile.quality_score}/100
              </Badge>
            )}
            {profile.industry && (
              <Badge variant="accent" size="sm">
                <Building2 className="w-3 h-3 mr-1" />
                {profile.industry}
              </Badge>
            )}
            {profile.website && (
              <a
                href={profile.website}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-[var(--asi-info-light)] text-[var(--asi-info)] hover:opacity-80 transition-opacity"
              >
                <Globe className="w-3 h-3" />
                Website
              </a>
            )}
            {profile.enriched_at && (
              <Badge variant="default" size="sm">
                <Calendar className="w-3 h-3 mr-1" />
                Enriched: {new Date(profile.enriched_at).toLocaleDateString()}
              </Badge>
            )}
          </div>

          {/* Markdown Content */}
          {profile.has_profile && profile.markdown ? (
            <div className="prose prose-sm prose-invert max-w-none">
              <div className="bg-[var(--asi-bg-secondary)] rounded-xl p-6 border border-[var(--asi-bg-tertiary)]">
                <ReactMarkdown
                  components={{
                    h1: ({ children }) => (
                      <h1 className="text-xl font-bold text-[var(--asi-text)] mb-4 pb-2 border-b border-[var(--asi-bg-tertiary)]">
                        {children}
                      </h1>
                    ),
                    h2: ({ children }) => (
                      <h2 className="text-lg font-semibold text-[var(--asi-text)] mt-6 mb-3">
                        {children}
                      </h2>
                    ),
                    h3: ({ children }) => (
                      <h3 className="text-base font-medium text-[var(--asi-text)] mt-4 mb-2">
                        {children}
                      </h3>
                    ),
                    p: ({ children }) => (
                      <p className="text-[var(--asi-text-secondary)] mb-3 leading-relaxed">
                        {children}
                      </p>
                    ),
                    ul: ({ children }) => (
                      <ul className="list-disc list-inside space-y-1 text-[var(--asi-text-secondary)] mb-4">
                        {children}
                      </ul>
                    ),
                    ol: ({ children }) => (
                      <ol className="list-decimal list-inside space-y-1 text-[var(--asi-text-secondary)] mb-4">
                        {children}
                      </ol>
                    ),
                    li: ({ children }) => (
                      <li className="text-[var(--asi-text-secondary)]">{children}</li>
                    ),
                    strong: ({ children }) => (
                      <strong className="font-semibold text-[var(--asi-text)]">{children}</strong>
                    ),
                    a: ({ href, children }) => (
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[var(--asi-accent)] hover:underline"
                      >
                        {children}
                      </a>
                    ),
                    blockquote: ({ children }) => (
                      <blockquote className="border-l-4 border-[var(--asi-accent)] pl-4 italic text-[var(--asi-text-muted)] my-4">
                        {children}
                      </blockquote>
                    ),
                    hr: () => <hr className="border-[var(--asi-bg-tertiary)] my-6" />,
                    table: ({ children }) => (
                      <div className="overflow-x-auto my-4">
                        <table className="w-full text-sm">{children}</table>
                      </div>
                    ),
                    th: ({ children }) => (
                      <th className="text-left py-2 px-3 bg-[var(--asi-bg-tertiary)] text-[var(--asi-text)] font-medium">
                        {children}
                      </th>
                    ),
                    td: ({ children }) => (
                      <td className="py-2 px-3 border-b border-[var(--asi-bg-tertiary)] text-[var(--asi-text-secondary)]">
                        {children}
                      </td>
                    ),
                  }}
                >
                  {profile.markdown}
                </ReactMarkdown>
              </div>
            </div>
          ) : (
            <div className="text-center py-12 bg-[var(--asi-bg-secondary)] rounded-xl">
              <FileText className="w-12 h-12 mx-auto mb-3 text-[var(--asi-text-muted)]" />
              <p className="text-[var(--asi-text-secondary)]">No profile available</p>
              <p className="text-sm text-[var(--asi-text-muted)] mt-1">
                Use /lead:enrich to generate a profile for this lead
              </p>
            </div>
          )}
        </div>
      )}
    </Drawer>
  );
}
